import { useEffect, useRef, useState } from 'react';

import type { ApiOrgId } from '#/api/types/ApiTypedId';
import { DropdownChevron } from '#/components/Dropdown/Chevron/DropdownChevron';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { DropdownMenu } from '#/components/Dropdown/menus/Menu/DropdownMenu';
import { DropdownMenuItem } from '#/components/Dropdown/menus/Menu/Item/DropdownMenuItem';
import { Icon } from '#/components/Icon/Icon';
import { useI18n } from '#/i18n/hooks/useI18n';
import { LayoutHeaderButton } from '#/layouts/Layout/Header/Button/LayoutHeaderButton';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { sumBy } from '#/utils/functional/array/sumBy';

import {
  CartDropdownButtonCount,
  CartDropdownButtonCountAndIconHolder,
  CartDropdownButtonMenuItemCount,
  CartDropdownButtonMenuItemEmptyMessage,
  CartDropdownButtonMenuItemLink,
  CartDropdownButtonMenuItemPostLink,
} from './CartDropdownButton.styled';
import type { CartDropdownButtonOrgData } from './CartDropdownButtonOrgData';

const cartDropdownButtonQaId = 'cart-dropdown-button';
const cartDropdownButtonCountQaId = 'cart-dropdown-button-count';

type Props = {
  selectedOrgId: ApiOrgId | undefined;
  getOrgCartDataHref: (orgCartData: CartDropdownButtonOrgData) => string;
  orgsCartData: CartDropdownButtonOrgData[];
  emptyHrefs: {
    postAJob: string;
    postAnInternship: string;
    postAVolop: string;
    postAnEvent: string;
  };
};

export function CartDropdownButton({
  selectedOrgId,
  getOrgCartDataHref,
  orgsCartData,
  emptyHrefs,
}: Props) {
  const { i18n } = useI18n();

  const [dropdownVariant, setDropdownVariant] = useState<
    'blue' | 'blueGreenAnimate'
  >('blue');

  const totalItemsCount = sumBy(orgsCartData, 'itemsCount');

  const prevCountRef = useRef<number>(null);

  const filteredOrgsCartData = orgsCartData.filter(
    (orgCartData) => orgCartData.itemsCount > 0,
  );

  useEffect(() => {
    if (
      prevCountRef.current !== undefined &&
      prevCountRef.current !== null &&
      totalItemsCount > prevCountRef.current
    ) {
      setDropdownVariant('blueGreenAnimate');
      setTimeout(() => {
        setDropdownVariant('blue');
      }, 3000);
    }
    prevCountRef.current = totalItemsCount;
  }, [totalItemsCount]);

  const countAndIcon = (
    <CartDropdownButtonCountAndIconHolder>
      {totalItemsCount > 0 && (
        <CartDropdownButtonCount data-qa-id={cartDropdownButtonCountQaId}>
          {totalItemsCount}
        </CartDropdownButtonCount>
      )}

      <Icon name="cart" size={24} />
    </CartDropdownButtonCountAndIconHolder>
  );

  if (orgsCartData.length === 1) {
    return (
      <LayoutHeaderButton
        type="link"
        href={getOrgCartDataHref(orgsCartData[0])}
        variant="blue"
        minWidth={51}
        qaId={cartDropdownButtonQaId}
        smallHorizontalPadding
        onClick={() => {
          trackClicked('Cart - Org', {
            context: 'Header',
          });
        }}
      >
        {countAndIcon}
      </LayoutHeaderButton>
    );
  }

  const isEmpty = filteredOrgsCartData.length === 0;

  return (
    <Dropdown
      trigger={
        <LayoutHeaderButton
          type="inert"
          variant={isEmpty ? 'grey' : dropdownVariant}
          minWidth={51}
          smallHorizontalPadding
          roundBottomWhenOpen
        >
          {countAndIcon}

          <DropdownChevron noMarginLeft />
        </LayoutHeaderButton>
      }
      onToggle={(open) =>
        open &&
        trackClicked('Cart', {
          context: 'Header',
        })
      }
      qaId={cartDropdownButtonQaId}
    >
      <DropdownMenu
        width={isEmpty ? '196px' : '240px'}
        qaId={
          isEmpty
            ? 'cart-dropdown-button-placeholder-dropdown-menu'
            : 'cart-dropdown-button-dropdown-menu'
        }
      >
        {isEmpty && (
          <>
            <DropdownMenuItem>
              <CartDropdownButtonMenuItemEmptyMessage>
                All your carts are empty
              </CartDropdownButtonMenuItemEmptyMessage>
            </DropdownMenuItem>

            <DropdownMenuItem>
              <CartDropdownButtonMenuItemPostLink
                to={emptyHrefs.postAJob}
                qaId="cart-dropdown-button-menu-post-a-job"
                onClick={() =>
                  trackClicked('Cart - Post a Job', { context: 'Header' })
                }
              >
                {i18n('Post a Job')}
              </CartDropdownButtonMenuItemPostLink>
            </DropdownMenuItem>

            <DropdownMenuItem>
              <CartDropdownButtonMenuItemPostLink
                to={emptyHrefs.postAnInternship}
                qaId="cart-dropdown-button-menu-post-an-internship"
                onClick={() =>
                  trackClicked('Cart - Post an Internship', {
                    context: 'Header',
                  })
                }
              >
                {i18n('Post an Internship')}
              </CartDropdownButtonMenuItemPostLink>
            </DropdownMenuItem>

            <DropdownMenuItem>
              <CartDropdownButtonMenuItemPostLink
                to={emptyHrefs.postAVolop}
                qaId="cart-dropdown-button-menu-post-a-volop"
                onClick={() =>
                  trackClicked('Cart - Post a Volunteer Opportunity', {
                    context: 'Header',
                  })
                }
              >
                {i18n('Post a Volunteer Opportunity')}
              </CartDropdownButtonMenuItemPostLink>
            </DropdownMenuItem>

            <DropdownMenuItem>
              <CartDropdownButtonMenuItemPostLink
                to={emptyHrefs.postAnEvent}
                qaId="cart-dropdown-button-menu-post-an-event"
                onClick={() =>
                  trackClicked('Cart - Post an Event', {
                    context: 'Header',
                  })
                }
              >
                {i18n('Post an Event')}
              </CartDropdownButtonMenuItemPostLink>
            </DropdownMenuItem>
          </>
        )}

        {filteredOrgsCartData.map((orgCartData) => (
          <DropdownMenuItem
            key={orgCartData.id}
            qaId={
              orgCartData.id === selectedOrgId
                ? 'cart-dropdown-button-menu-item-selected'
                : undefined
            }
          >
            <CartDropdownButtonMenuItemLink
              to={getOrgCartDataHref(orgCartData)}
              $isSelected={orgCartData.id === selectedOrgId}
              onClick={() => {
                trackClicked('Cart - Org', {
                  context: 'Header',
                });
              }}
            >
              <CartDropdownButtonMenuItemCount data-qa-id="cart-dropdown-button-menu-item-count">
                {orgCartData.itemsCount}
              </CartDropdownButtonMenuItemCount>
              <span data-qa-id="cart-dropdown-button-menu-item-org-name">
                {orgCartData.name}
              </span>
            </CartDropdownButtonMenuItemLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}
